/**
 * Community Hub API Service
 *
 * API functions for the Community Hub feature
 */

import { api } from './api';
import type {
  TermsStatusResponse,
  TermsAcceptResponse,
  PostsResponse,
  PostResponse,
  CommentsResponse,
  CommentResponse,
  LikeResponse,
  ReportReasonsResponse,
  ReportResponse,
  ReportsResponse,
  BlocksResponse,
  MessageResponse,
  CreatePostRequest,
  UpdatePostRequest,
  CreateCommentRequest,
  UpdateCommentRequest,
  CreateReportRequest,
  UpdateReportRequest,
  BlockUserRequest,
  PostFilters,
  CommentFilters,
  ReportFilters,
} from '../types/community';

// Base URL for community endpoints
const COMMUNITY_URL = '/community';

// ============================================
// TERMS ENDPOINTS
// ============================================

/**
 * Check if user has accepted current terms version
 */
export const getTermsStatus = async (): Promise<TermsStatusResponse> => {
  const response = await api.get<TermsStatusResponse>(`${COMMUNITY_URL}/terms/status`);
  return response.data;
};

/**
 * Accept current terms version
 */
export const acceptTerms = async (): Promise<TermsAcceptResponse> => {
  const response = await api.post<TermsAcceptResponse>(`${COMMUNITY_URL}/terms/accept`);
  return response.data;
};

// ============================================
// POST ENDPOINTS
// ============================================

/**
 * Get community feed posts
 */
export const getPosts = async (filters?: PostFilters): Promise<PostsResponse> => {
  const params = new URLSearchParams();

  if (filters) {
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    if (filters.limit) {
      params.append('limit', filters.limit.toString());
    }
  }

  const queryString = params.toString();
  const url = queryString ? `${COMMUNITY_URL}/posts?${queryString}` : `${COMMUNITY_URL}/posts`;

  const response = await api.get<PostsResponse>(url);
  return response.data;
};

/**
 * Get a single post by ID
 */
export const getPost = async (id: string): Promise<PostResponse> => {
  const response = await api.get<PostResponse>(`${COMMUNITY_URL}/posts/${id}`);
  return response.data;
};

/**
 * Create a new post
 */
export const createPost = async (data: CreatePostRequest): Promise<PostResponse> => {
  const response = await api.post<PostResponse>(`${COMMUNITY_URL}/posts`, data);
  return response.data;
};

/**
 * Update a post
 */
export const updatePost = async (id: string, data: UpdatePostRequest): Promise<PostResponse> => {
  const response = await api.patch<PostResponse>(`${COMMUNITY_URL}/posts/${id}`, data);
  return response.data;
};

/**
 * Delete a post
 */
export const deletePost = async (id: string): Promise<MessageResponse> => {
  const response = await api.delete<MessageResponse>(`${COMMUNITY_URL}/posts/${id}`);
  return response.data;
};

/**
 * Toggle like on a post
 */
export const togglePostLike = async (id: string): Promise<LikeResponse> => {
  const response = await api.post<LikeResponse>(`${COMMUNITY_URL}/posts/${id}/like`);
  return response.data;
};

// ============================================
// COMMENT ENDPOINTS
// ============================================

/**
 * Get comments for a post
 */
export const getComments = async (
  postId: string,
  filters?: CommentFilters
): Promise<CommentsResponse> => {
  const params = new URLSearchParams();

  if (filters) {
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    if (filters.limit) {
      params.append('limit', filters.limit.toString());
    }
  }

  const queryString = params.toString();
  const url = queryString
    ? `${COMMUNITY_URL}/posts/${postId}/comments?${queryString}`
    : `${COMMUNITY_URL}/posts/${postId}/comments`;

  const response = await api.get<CommentsResponse>(url);
  return response.data;
};

/**
 * Create a comment on a post
 */
export const createComment = async (
  postId: string,
  data: CreateCommentRequest
): Promise<CommentResponse> => {
  const response = await api.post<CommentResponse>(
    `${COMMUNITY_URL}/posts/${postId}/comments`,
    data
  );
  return response.data;
};

/**
 * Update a comment
 */
export const updateComment = async (
  id: string,
  data: UpdateCommentRequest
): Promise<CommentResponse> => {
  const response = await api.patch<CommentResponse>(`${COMMUNITY_URL}/comments/${id}`, data);
  return response.data;
};

/**
 * Delete a comment
 */
export const deleteComment = async (id: string): Promise<MessageResponse> => {
  const response = await api.delete<MessageResponse>(`${COMMUNITY_URL}/comments/${id}`);
  return response.data;
};

/**
 * Toggle like on a comment
 */
export const toggleCommentLike = async (id: string): Promise<LikeResponse> => {
  const response = await api.post<LikeResponse>(`${COMMUNITY_URL}/comments/${id}/like`);
  return response.data;
};

/**
 * Get replies for a comment
 */
export const getReplies = async (
  commentId: string,
  filters?: CommentFilters
): Promise<{ success: boolean; data: { replies: unknown[]; pagination: unknown } }> => {
  const params = new URLSearchParams();

  if (filters) {
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    if (filters.limit) {
      params.append('limit', filters.limit.toString());
    }
  }

  const queryString = params.toString();
  const url = queryString
    ? `${COMMUNITY_URL}/comments/${commentId}/replies?${queryString}`
    : `${COMMUNITY_URL}/comments/${commentId}/replies`;

  const response = await api.get(url);
  return response.data;
};

// ============================================
// REPORT ENDPOINTS
// ============================================

/**
 * Get report reasons
 */
export const getReportReasons = async (): Promise<ReportReasonsResponse> => {
  const response = await api.get<ReportReasonsResponse>(`${COMMUNITY_URL}/reports/reasons`);
  return response.data;
};

/**
 * Report a post
 */
export const reportPost = async (id: string, data: CreateReportRequest): Promise<ReportResponse> => {
  const response = await api.post<ReportResponse>(`${COMMUNITY_URL}/posts/${id}/report`, data);
  return response.data;
};

/**
 * Report a comment
 */
export const reportComment = async (
  id: string,
  data: CreateReportRequest
): Promise<ReportResponse> => {
  const response = await api.post<ReportResponse>(`${COMMUNITY_URL}/comments/${id}/report`, data);
  return response.data;
};

// ============================================
// ADMIN ENDPOINTS
// ============================================

/**
 * Get pending reports (Super Admin)
 */
export const getReports = async (filters?: ReportFilters): Promise<ReportsResponse> => {
  const params = new URLSearchParams();

  if (filters) {
    if (filters.status) {
      params.append('status', filters.status);
    }
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    if (filters.limit) {
      params.append('limit', filters.limit.toString());
    }
  }

  const queryString = params.toString();
  const url = queryString
    ? `${COMMUNITY_URL}/admin/reports?${queryString}`
    : `${COMMUNITY_URL}/admin/reports`;

  const response = await api.get<ReportsResponse>(url);
  return response.data;
};

/**
 * Update report status (Super Admin)
 */
export const updateReport = async (
  id: string,
  data: UpdateReportRequest
): Promise<{ success: boolean; data: { report: unknown } }> => {
  const response = await api.patch(`${COMMUNITY_URL}/admin/reports/${id}`, data);
  return response.data;
};

/**
 * Hide a post (Super Admin)
 */
export const hidePost = async (
  id: string,
  reason?: string
): Promise<MessageResponse> => {
  const response = await api.post<MessageResponse>(`${COMMUNITY_URL}/admin/posts/${id}/hide`, {
    reason,
  });
  return response.data;
};

/**
 * Unhide a post (Super Admin)
 */
export const unhidePost = async (id: string): Promise<MessageResponse> => {
  const response = await api.post<MessageResponse>(`${COMMUNITY_URL}/admin/posts/${id}/unhide`);
  return response.data;
};

/**
 * Hide a comment (Super Admin)
 */
export const hideComment = async (
  id: string,
  reason?: string
): Promise<MessageResponse> => {
  const response = await api.post<MessageResponse>(`${COMMUNITY_URL}/admin/comments/${id}/hide`, {
    reason,
  });
  return response.data;
};

/**
 * Pin/unpin a post (Super Admin)
 */
export const togglePostPin = async (
  id: string,
  pinned: boolean
): Promise<MessageResponse> => {
  const response = await api.post<MessageResponse>(`${COMMUNITY_URL}/admin/posts/${id}/pin`, {
    pinned,
  });
  return response.data;
};

/**
 * Get blocked users (Super Admin)
 */
export const getBlockedUsers = async (
  page?: number,
  limit?: number
): Promise<BlocksResponse> => {
  const params = new URLSearchParams();
  if (page) params.append('page', page.toString());
  if (limit) params.append('limit', limit.toString());

  const queryString = params.toString();
  const url = queryString
    ? `${COMMUNITY_URL}/admin/blocks?${queryString}`
    : `${COMMUNITY_URL}/admin/blocks`;

  const response = await api.get<BlocksResponse>(url);
  return response.data;
};

/**
 * Block a user (Super Admin)
 */
export const blockUser = async (
  userId: string,
  data: BlockUserRequest
): Promise<MessageResponse> => {
  const response = await api.post<MessageResponse>(
    `${COMMUNITY_URL}/admin/users/${userId}/block`,
    data
  );
  return response.data;
};

/**
 * Unblock a user (Super Admin)
 */
export const unblockUser = async (userId: string): Promise<MessageResponse> => {
  const response = await api.post<MessageResponse>(
    `${COMMUNITY_URL}/admin/users/${userId}/unblock`
  );
  return response.data;
};

// Export all functions as default object for convenience
export default {
  // Terms
  getTermsStatus,
  acceptTerms,
  // Posts
  getPosts,
  getPost,
  createPost,
  updatePost,
  deletePost,
  togglePostLike,
  // Comments
  getComments,
  createComment,
  updateComment,
  deleteComment,
  toggleCommentLike,
  getReplies,
  // Reports
  getReportReasons,
  reportPost,
  reportComment,
  // Admin
  getReports,
  updateReport,
  hidePost,
  unhidePost,
  hideComment,
  togglePostPin,
  getBlockedUsers,
  blockUser,
  unblockUser,
};
